import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Grid,
  IconButton,
  Chip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { WhatsAppUsage } from '../types/Site';

const WhatsAppUsageManager: React.FC = () => {
  const [usage, setUsage] = useState<WhatsAppUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success] = useState<string | null>(null);
  
  // Settings state
  const [, setDailyLimit] = useState<number>(100);
  const [, setMonthlyLimit] = useState<number>(1000);
  const [isEnabled, setIsEnabled] = useState<boolean>(true);
  
  useEffect(() => {
    fetchUsage();
  }, []);

  useEffect(() => {
    if (usage) {
      setDailyLimit(usage.dailyLimit);
      setMonthlyLimit(usage.monthlyLimit);
      setIsEnabled(usage.isEnabled);
    }
  }, [usage]);

  const fetchUsage = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/sites/whatsapp/usage`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setUsage(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || '獲取使用情況失敗');
    } finally {
      setLoading(false);
    }
  };

  const getProgressColor = (current: number, limit: number) => {
    const percentage = (current / limit) * 100;
    if (percentage >= 90) return 'error';
    if (percentage >= 70) return 'warning';
    return 'primary';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-HK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            WhatsApp 使用情況
          </Typography>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              WhatsApp 使用情況
            </Typography>
            <Box>
              <IconButton onClick={fetchUsage} title="刷新">
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            重置使用統計功能僅限全域管理員使用。如需重置統計數據，請聯繫系統管理員。
          </Alert>

          {usage && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box mb={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      每日使用量
                    </Typography>
                    <Chip 
                      label={isEnabled ? '已啟用' : '已停用'} 
                      color={isEnabled ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min((usage.dailyCount / usage.dailyLimit) * 100, 100)}
                    color={getProgressColor(usage.dailyCount, usage.dailyLimit)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {usage.dailyCount} / {usage.dailyLimit} (剩餘: {usage.dailyRemaining})
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary" mb={1}>
                    每月使用量
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min((usage.monthlyCount / usage.monthlyLimit) * 100, 100)}
                    color={getProgressColor(usage.monthlyCount, usage.monthlyLimit)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {usage.monthlyCount} / {usage.monthlyLimit} (剩餘: {usage.monthlyRemaining})
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  總發送數: {usage.totalSent} | 
                  每日重置: {formatDate(usage.lastResetDate)} | 
                  每月重置: {formatDate(usage.lastMonthlyResetDate)}
                </Typography>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default WhatsAppUsageManager; 